import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Typography, Tag, Tooltip, App, Breadcrumb,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useAuth } from '../context/AuthContext';
import { usersApi } from '../api/users';
import { User } from '../api/types';

const { Title } = Typography;

const UsersPage: React.FC = () => {
  const { apiClient } = useAuth();
  const { message, modal } = App.useApp();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const api = useCallback(() => usersApi(apiClient!), [apiClient]);

  const fetchUsers = useCallback(async (p: number, ps: number) => {
    setLoading(true);
    try {
      const res = await api().getList(p, ps);
      setUsers(res.data.items);
      setTotal(res.data.total);
    } catch {
      message.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [api, message]);

  useEffect(() => { fetchUsers(page, pageSize); }, [fetchUsers, page, pageSize]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPage(pagination.current ?? 1);
    setPageSize(pagination.pageSize ?? 20);
  };

  const handleDelete = (user: User) => {
    modal.confirm({
      title: 'Delete user?',
      content: `This will permanently delete "${user.username}" (${user.email}). This cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api().deleteUser(user.userId);
          message.success('User deleted');
          const isLastOnPage = users.length === 1 && page > 1;
          if (isLastOnPage) {
            setPage(p => p - 1);
          } else {
            fetchUsers(page, pageSize);
          }
        } catch (err: any) {
          if (err?.response?.status === 404) {
            message.error('User not found');
          } else {
            message.error('Failed to delete user');
          }
        }
      },
    });
  };

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'userId',
      width: 70,
    },
    {
      title: 'Username',
      dataIndex: 'username',
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Email Verified',
      dataIndex: 'isEmailVerified',
      width: 140,
      render: (v: boolean) => (
        <Tag color={v ? 'success' : 'default'}>{v ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: '',
      width: 80,
      render: (_, record) => (
        <Tooltip title="Delete user">
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => { e.stopPropagation(); handleDelete(record); }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <Breadcrumb items={[{ title: 'Users' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>Users</Title>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        rowKey="userId"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (t) => `${t} users`,
        }}
        onChange={handleTableChange}
      />
    </Space>
  );
};

export default UsersPage;
